import { useSnackbar } from 'hephaestus/web';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTradingHub } from '../tradingHubContext';
import type { PortfolioReport } from '../types';
import {
  calculateAggregateExposure,
  calculateModelAggregation,
} from '../utils';
import {
  buildExposureSubTradeRequests,
  type ExposureTradeDirection,
  type ExposureTradeSelection,
} from '../utils/createExposureSubTradeRequests';
import {
  BUCKET_SUB_TRADE_REQUESTS,
  CREATE_SUB_TRADE_REQUESTS,
} from '../utils/mutations';
import type { ViewMode } from '../utils/portfolioFilters';
import type { EnrichedPortfolioReport } from './usePortfolioTableState.types';
import { useTradingMutation } from './useTradingMutation';

export function usePortfolioBulkActions({
  organizationId,
  bulkTradingProcess,
  filteredPortfolios,
  displayedPortfolios,
  viewMode,
}: {
  organizationId?: string;
  bulkTradingProcess?: string;
  filteredPortfolios: EnrichedPortfolioReport[];
  displayedPortfolios: EnrichedPortfolioReport[];
  viewMode: ViewMode;
}) {
  const {
    setActiveFlowStep,
    setCurrentTab,
    triggerRefresh,
    setLastBulkCreationTime,
  } = useTradingHub();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('portfolioTradingHub');

  const [selectedPortfolios, setSelectedPortfolios] = useState<Set<string>>(
    new Set(),
  );
  const [showReviewCommit, setShowReviewCommit] = useState(false);
  const [modelStagingPortfolios, setModelStagingPortfolios] = useState<
    PortfolioReport[] | null
  >(null);

  const [createSubTradeRequestsFromExposure] = useTradingMutation(
    CREATE_SUB_TRADE_REQUESTS,
  );
  const [bucketSubTradeRequestsFromExposure] = useTradingMutation(
    BUCKET_SUB_TRADE_REQUESTS,
  );

  useEffect(() => {
    const visibleIds = new Set(filteredPortfolios.map((p) => p.id));
    setSelectedPortfolios((prev) => {
      let changed = false;
      for (const id of prev) {
        if (!visibleIds.has(id)) {
          changed = true;
          break;
        }
      }
      if (!changed) return prev;

      const next = new Set<string>();
      for (const id of prev) {
        if (visibleIds.has(id)) next.add(id);
      }
      return next;
    });
  }, [filteredPortfolios]);

  const handleTradeComplete = useCallback(() => {
    setLastBulkCreationTime(Date.now());
    setActiveFlowStep('verifying');
    triggerRefresh();
  }, [setLastBulkCreationTime, setActiveFlowStep, triggerRefresh]);

  const completeRebalanceFlow = useCallback(
    ({
      clearSelection,
      closeReviewCommit = true,
    }: {
      clearSelection: boolean;
      closeReviewCommit?: boolean;
    }) => {
      if (closeReviewCommit) setShowReviewCommit(false);
      if (clearSelection) setSelectedPortfolios(new Set());
      setActiveFlowStep('verifying');
      setCurrentTab('tradeRequests');
      triggerRefresh();
    },
    [setActiveFlowStep, setCurrentTab, triggerRefresh],
  );

  const handleToggleSelect = useCallback((reportId: string) => {
    setSelectedPortfolios((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedPortfolios(new Set(displayedPortfolios.map((r) => r.id)));
      } else {
        setSelectedPortfolios(new Set());
      }
    },
    [displayedPortfolios],
  );

  const selectedPortfolioReports = useMemo(
    () => filteredPortfolios.filter((p) => selectedPortfolios.has(p.id)),
    [filteredPortfolios, selectedPortfolios],
  );

  const handleCreateTradesFromExposure = useCallback(
    async (
      selections: ExposureTradeSelection[],
      direction: ExposureTradeDirection,
      buyAmountCentsPerAccount?: number,
    ) => {
      try {
        const result = buildExposureSubTradeRequests(
          selections,
          direction,
          buyAmountCentsPerAccount,
        );

        if (result.status === 'no_actionable') {
          if (result.skippedGoalCount > 0) {
            enqueueSnackbar(
              t('portfoliosTable.tradeToasts.missingGoalObjectId', {
                count: result.skippedGoalCount,
              }),
              { variant: 'warning' },
            );
          } else {
            enqueueSnackbar(t('portfoliosTable.tradeToasts.noActionable'), {
              variant: 'info',
            });
          }
          return;
        }

        if (result.skippedGoalCount > 0) {
          enqueueSnackbar(
            t('portfoliosTable.tradeToasts.missingGoalObjectId', {
              count: result.skippedGoalCount,
            }),
            { variant: 'warning' },
          );
        }

        const createResults = await Promise.allSettled(
          result.groups.map(({ source, sourceId, trades }) => {
            const subTradeRequests = trades.map(
              ({
                subAccountId,
                financialProductId,
                portfolioReportId,
                type,
                moneyAllocatedCents,
                currency,
              }) => ({
                subAccountId,
                financialProductId,
                portfolioReportId,
                type,
                moneyAllocatedCents,
                currency,
              }),
            );
            return createSubTradeRequestsFromExposure({
              variables: { input: { source, sourceId, subTradeRequests } },
            });
          }),
        );

        const succeededGroupIndexes = createResults.flatMap((r, i) =>
          r.status === 'fulfilled' ? [i] : [],
        );
        const createSucceeded = succeededGroupIndexes.length;
        const createFailed = createResults.length - createSucceeded;

        if (createSucceeded === 0) {
          enqueueSnackbar(t('portfoliosTable.tradeToasts.createAllFailed'), {
            variant: 'error',
          });
          return;
        }

        if (createFailed > 0) {
          enqueueSnackbar(
            t('portfoliosTable.tradeToasts.createPartialSuccess', {
              succeeded: createSucceeded,
              total: createResults.length,
              failed: createFailed,
            }),
            { variant: 'warning' },
          );
        }

        let bucketFailures = 0;
        if (bulkTradingProcess === 'BUCKET') {
          // Only bucket groups whose create succeeded — failed creates have
          // nothing to bucket and would inflate bucketFailures noise.
          const bucketResults = await Promise.allSettled(
            succeededGroupIndexes.map((i) => {
              const { source, sourceId } = result.groups[i];
              return bucketSubTradeRequestsFromExposure({
                variables: {
                  input: {
                    source,
                    sourceId,
                    ...(organizationId && { organizationId }),
                  },
                },
              });
            }),
          );
          bucketFailures = bucketResults.filter(
            (r) => r.status === 'rejected',
          ).length;
        }

        // Skip success toast on partial create — createPartialSuccess already
        // covers that case; count only trades from fulfilled groups.
        if (createFailed === 0) {
          const createdTradeCount = succeededGroupIndexes.reduce(
            (sum, i) => sum + result.groups[i].trades.length,
            0,
          );
          enqueueSnackbar(
            t('portfoliosTable.tradeToasts.created', {
              count: createdTradeCount,
            }),
            { variant: 'success' },
          );
        }

        if (bucketFailures > 0) {
          enqueueSnackbar(
            t('portfoliosTable.tradeToasts.bucketPartialFailure', {
              count: bucketFailures,
            }),
            { variant: 'warning' },
          );
        }

        handleTradeComplete();
      } catch {
        // Error toast handled by useTradingMutation
      }
    },
    [
      bulkTradingProcess,
      organizationId,
      createSubTradeRequestsFromExposure,
      bucketSubTradeRequestsFromExposure,
      enqueueSnackbar,
      t,
      handleTradeComplete,
    ],
  );

  const aggregateExposures = useMemo(
    () =>
      viewMode === 'exposure'
        ? calculateAggregateExposure(filteredPortfolios)
        : [],
    [filteredPortfolios, viewMode],
  );

  const modelAggregations = useMemo(
    () =>
      viewMode === 'model' ? calculateModelAggregation(filteredPortfolios) : [],
    [filteredPortfolios, viewMode],
  );

  return {
    selectedPortfolios,
    setSelectedPortfolios,
    showReviewCommit,
    setShowReviewCommit,
    modelStagingPortfolios,
    setModelStagingPortfolios,
    handleToggleSelect,
    handleSelectAll,
    handleCreateTradesFromExposure,
    handleTradeComplete,
    completeRebalanceFlow,
    selectedPortfolioReports,
    aggregateExposures,
    modelAggregations,
  };
}
